import React, { useState } from 'react';
import { Card, CardContent, Typography, Button, Chip, Box } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import { offlineApiService } from '../services/offlineApi';
import { toast } from 'react-hot-toast';

interface Task {
  _id: string;
  title: string;
  description: string;
  status: 'pending' | 'completed';
  xpReward: number;
  dueDate?: string;
  plantId?: string;
  gardenId?: string;
}

interface TaskCardProps {
  task: Task;
  onComplete?: (taskId: string) => void;
}

const TaskCard: React.FC<TaskCardProps> = ({ task, onComplete }) => {
  const [isCompleting, setIsCompleting] = useState(false);
  const [localStatus, setLocalStatus] = useState(task.status);

  const handleComplete = async () => {
    if (localStatus === 'completed') return;

    setIsCompleting(true);
    setLocalStatus('completed');

    try {
      const response = await offlineApiService.updateTaskStatus(task._id, 'completed');
      
      if (response.queued) {
        toast.success('Task marked complete! Will sync when online.', {
          icon: <WifiOffIcon sx={{ width: 16, height: 16 }} />,
        });
      } else {
        toast.success(`Task completed! +${task.xpReward} XP`);
      }

      if (onComplete) {
        onComplete(task._id);
      }
    } catch (error: any) {
      // Check if it was queued offline
      if (error.response?.data?.queued) {
        toast.success('Task marked complete! Will sync when online.', {
          icon: <WifiOffIcon sx={{ width: 16, height: 16 }} />,
        });
        if (onComplete) {
          onComplete(task._id);
        }
      } else {
        // Real error - revert the status
        setLocalStatus(task.status);
        toast.error('Failed to complete task');
        console.error('Error completing task:', error);
      }
    } finally {
      setIsCompleting(false);
    }
  };

  const isCompleted = localStatus === 'completed';

  return (
    <Card 
      sx={{ 
        mb: 2, 
        opacity: isCompleted ? 0.7 : 1,
        transition: 'all 0.3s ease',
      }}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="start">
          <Box flex={1}>
            <Typography 
              variant="h6" 
              sx={{ 
                textDecoration: isCompleted ? 'line-through' : 'none',
                color: isCompleted ? 'text.secondary' : 'text.primary',
              }}
            >
              {task.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {task.description}
            </Typography>
            
            <Box display="flex" gap={1} mt={2}>
              <Chip 
                label={`${task.xpReward} XP`} 
                size="small" 
                color="primary" 
                variant="outlined"
              />
              {task.dueDate && (
                <Chip 
                  icon={<AccessTimeIcon sx={{ width: 16, height: 16 }} />}
                  label={new Date(task.dueDate).toLocaleDateString()} 
                  size="small" 
                  variant="outlined"
                />
              )}
            </Box>
          </Box>

          <Button
            onClick={handleComplete}
            disabled={isCompleted || isCompleting}
            startIcon={<CheckCircleIcon />}
            variant={isCompleted ? "outlined" : "contained"}
            color="primary"
            size="small"
            sx={{ ml: 2, minWidth: 100 }}
          >
            {isCompleted ? 'Done' : isCompleting ? 'Saving...' : 'Complete'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default TaskCard;